// Angular
import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
// RxJS
import {BehaviorSubject, Observable, Subject} from 'rxjs';
// Translate
import {TranslateService} from '@ngx-translate/core';
// Store
import {select, Store} from '@ngrx/store';
import {AppState} from '../../../../core/reducers';
// Auth
import {AuthActions, AuthNoticeService, error, isLoading} from '../../../../core/auth';
import {Token} from '../../../../core/auth/_models/token';
import {EkFooterInformationModel} from '../../../../core/ek-e-commerce/ek-models/EkfooterInformation.model';
import {OtpService} from '../../../../core/auth/_services/otp.service';
import {MustMatch} from '../../dashboard/profile/profile/profile.component';
import {AuthService} from '../../../../core/auth/_services/auth.service';
import {LayoutUtilsService, MessageType} from '../../../../core/_base/crud';
import {UserService} from '../../../../core/services/user.service';
import {DOCUMENT} from '@angular/common';
import {Actions, ofType} from '@ngrx/effects';

// Selectors
/**
 * ! Just example => Should be removed in development
 */


@Component({
    selector: 'kt-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy {
    // Public params
    loginForm: FormGroup;
    loading = false;
    errors: any = [];
    private unsubscribe: Subject<any>;
    private returnUrl: any;

    //My Observables
    isLoggedIn$: Observable<boolean>;
    error$: Observable<any>;
    isLoading$: Observable<boolean>;
    currentUserRoles$: Observable<string>;
    token$: Observable<Token>;
    hide = true;
    phoneNumber = '0770343828';
    footerInformations: EkFooterInformationModel;
    otpSent = false;

    passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{12,}$/;

    otpSent$ = new BehaviorSubject<boolean>(false);


    // logging in progress
    loginIsLoading$ = new BehaviorSubject<boolean>(false);
    loginLoading$ = this.loginIsLoading$.asObservable();


    changePsw$ = new BehaviorSubject<boolean>(false);

    // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

    /**
     * Component constructor
     *
     * @param router: Router
     * @param auth: AuthService
     * @param authNoticeService: AuthNoticeService
     * @param translate: TranslateService
     * @param store: Store<AppState>
     * @param fb: FormBuilder
     * @param cdr
     * @param route
     */
    constructor(
        @Inject(DOCUMENT) private document: Document,
        private authNoticeService: AuthNoticeService,
        private translate: TranslateService,
        private store: Store<AppState>,
        private fb: FormBuilder,
        private actions$: Actions,
        private cdr: ChangeDetectorRef,
        private otpService: OtpService,
        private userService: UserService,
        private authService: AuthService,
        private layoutUtilsService: LayoutUtilsService,
    ) {
        this.unsubscribe = new Subject();
    }

    /**
     * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
     */

    /**
     * On init
     */

    ngOnInit() {
        this.initLoginForm();

        this.loginForm.valueChanges.subscribe(() => {
            if (this.loginForm.dirty) {
                this.loginIsLoading$.next(false);
            }
        });
    }

    initLoginForm() {
        this.loginForm = this.fb.group({
            email: ['', [Validators.required, Validators.email, Validators.minLength(3), Validators.maxLength(320)]],
            password: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
            otp: this.fb.group({  // nested group for 6 digits
                digit1: ['', [Validators.pattern(/^\d$/)]],
                digit2: ['', [Validators.pattern(/^\d$/)]],
                digit3: ['', [Validators.pattern(/^\d$/)]],
                digit4: ['', [Validators.pattern(/^\d$/)]],
                digit5: ['', [Validators.pattern(/^\d$/)]],
                digit6: ['', [Validators.pattern(/^\d$/)]],
            }),
            newPsw: ['', Validators.pattern(this.passwordPattern)],  // new psw
            ConfirmNewPsw: ['', Validators.pattern(this.passwordPattern)],  // confirm new psw
        }, {
            validator: MustMatch('newPsw', 'ConfirmNewPsw')
        });
    }

    onSubmit() {

        if (!this.otpSent) {
            // étape 1 : envoyer OTP
            if (!this.changePsw$.value) {
                if (this.loginForm.get('email').invalid || this.loginForm.get('password').invalid) {
                    this.loginForm.get('email').markAsTouched();
                    this.loginForm.get('password').markAsTouched();
                    return;
                }
            } else {
                const newPswControl = this.loginForm.get('newPsw');
                const ConfirmNewPswPswControl = this.loginForm.get('ConfirmNewPsw');
                if (ConfirmNewPswPswControl && newPswControl) {
                    newPswControl.setValidators([Validators.required]);
                    newPswControl.updateValueAndValidity();
                    ConfirmNewPswPswControl.setValidators([Validators.required]);
                    ConfirmNewPswPswControl.updateValueAndValidity();
                }
                this.cdr.detectChanges(); // facultatif avec async pipe

                if (this.loginForm.get('newPsw').invalid || this.loginForm.get('ConfirmNewPsw').invalid) {
                    this.loginForm.get('newPsw').markAsTouched();
                    this.loginForm.get('ConfirmNewPsw').markAsTouched();
                    return;
                } else {
                    this.updateNewPsw();
                    return;
                }
            }
            this.sendOtp();
        } else {
            // étape 2 : valider OTP
            const otpGroup = this.loginForm.get('otp') as FormGroup;

            if (otpGroup.invalid) {
                // Mark all controls as touched to show errors
                Object.values(otpGroup.controls).forEach(control => control.markAsTouched());
                return;
            }
            this.validateOtp();
        }
    }

    sendOtp() {
        const email = this.loginForm.get('email')?.value;
        const password = this.loginForm.get('password')?.value;
        this.loading = true;
        this.loginIsLoading$.next(true);

        // if stg/dev/local do not request otp
        if (this.document.baseURI.includes('https://staging.admin.ekiclik.com')
            || this.document.baseURI.includes('https://release-admin.ekiclik.com')
            || this.document.baseURI.includes('https://alsalamalgeria.pre-prod.admin.ekiclik.dz')
            || this.document.baseURI.includes('http://localhost:4200')) {
            this.submit();
        } else {
            this.otpService.sendOtp(email, password).subscribe({
                next: (response: string) => {
                    switch (response) {
                        case 'OTP has been sent to your registered phone number.' :
                            this.otpSent$.next(true);
                            this.otpSent = true;
                            this.loading = false;
                            this.loginIsLoading$.next(false);

                            const msg = 'Un code de vérification a été envoyé à votre numéro de téléphone enregistré.';
                            this.authNoticeService.setNotice(msg, 'information');

                            const otpGroup = this.loginForm.get('otp') as FormGroup;

                            Object.values(otpGroup.controls).forEach(control => {
                                control.setValidators([Validators.required, Validators.pattern(/^\d$/)]);
                                control.updateValueAndValidity();
                            });

                            this.cdr.detectChanges(); // facultatif avec async pipe
                            break;
                        default:
                            // Si la réponse est différente, afficher une erreur
                            this.loading = false;
                            this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
                    }
                },
                error: (error) => {
                    this.loading = false;
                    this.loginIsLoading$.next(false);
                    if (error?.error?.includes('User with username or email =')) {
                        this.authNoticeService.setNotice(
                            'Échec de la connexion : utilisateur introuvable. Veuillez vérifier vos identifiants ou contacter le support au ' + this.phoneNumber,
                            'danger'
                        );
                    } else {
                        switch (error?.error) {
                            case 'Login failed: Archived or inactive user':
                                this.authNoticeService.setNotice(
                                    "Échec de la connexion : votre compte est inactif ou archivé. Veuillez contacter le support au " + this.phoneNumber,
                                    "danger"
                                );
                                break;
                            case 'Your account is locked due to multiple failed login attempts.' :
                                this.authNoticeService.setNotice('Votre compte a été verrouillé en raison de trop nombreuses tentatives de connexion échouées.',
                                    'danger');
                                break;
                            case 'Your password has expired. Please change your password.' :
                                this.changePsw$.next(true);
                                this.authNoticeService.setNotice('Votre mot de passe a expiré. Veuillez le changer pour continuer.',
                                    'danger');
                                break;
                            case 'Login failed: Invalid credentials.' :
                                this.authNoticeService.setNotice('Échec de la connexion : identifiants incorrects.',
                                    'danger');
                                break;
                            default:
                                this.authNoticeService.setNotice("Erreur lors de l’envoi du code OTP.", "danger");
                        }
                    }

                }
            });
        }

    }

    markFormGroupTouched(formGroup: FormGroup) {
        Object.values(formGroup.controls).forEach(control => {
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control); // recursive
            } else {
                control.markAsTouched();
            }
        });
    }

    submit() {
        const controls = this.loginForm.controls;

        /** check form */
        if (this.loginForm.invalid) {
            this.markFormGroupTouched(this.loginForm);
            console.log('invalid');
            return;
        }

        const authData = {
            email: controls.email.value,
            password: controls.password.value
        };


        // dispatch action
        this.store.dispatch(AuthActions.login({email: authData.email, password: authData.password}));

        // Listen directly to loginSucceeded action
        this.actions$
            .pipe(ofType(AuthActions.loginSucceeded))
            .subscribe(() => {
                this.loginIsLoading$.next(false);
            });

        // Listen directly to loginFailed action
        this.actions$
            .pipe(ofType(AuthActions.loginFailed))
            .subscribe(() => {
                //this.loginIsLoading$.next(false);
            });

        this.isLoading$.subscribe((response) => {
            this.loading = response;
        });

        this.error$.subscribe((response) => {
            if (response) {
                this.loginIsLoading$.next(false);
                this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
            }
        });

        this.currentUserRoles$.subscribe((roles) => this.userService.selectedUserRolesSubject.next(roles));
    }

    validateOtp() {
        const email = this.loginForm.get('email')?.value;
        const otp = this.getFullOtp();
        this.loading = true;
        this.loginIsLoading$.next(true);
        this.otpService.validateOtp(email, otp).subscribe({
            next: (isValid: boolean) => {
                this.loading = false;
                if (isValid) {
                    this.performLogin(); // Authentification finale
                } else {
                    this.authNoticeService.setNotice('OTP invalide ou expiré', 'danger');
                }
            },
            error: () => {
                this.loading = false;
                this.loginIsLoading$.next(false);
                this.authNoticeService.setNotice('Erreur serveur lors de la validation du OTP', 'danger');
            },
        });
    }


    performLogin() {
        const email = this.loginForm.get('email').value;
        const password = this.loginForm.get('password').value;

        this.store.dispatch(AuthActions.login({email, password}));
        this.isLoading$ = this.store.pipe(select(isLoading));
        this.error$ = this.store.pipe(select(error));

        this.error$.subscribe(error => {
            if (error) {
                this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
            }
        });
    }

    isControlHasError(controlName: string, validationType: string): boolean {
        const control = this.loginForm.controls[controlName];
        if (!control) {
            return false;
        }
        return control.hasError(validationType) && (control.dirty || control.touched);
    }


    updateNewPsw() {
        const email = this.loginForm.get('email').value;
        const oldPassword = this.loginForm.get('password').value;
        const newPassword = this.loginForm.get('ConfirmNewPsw').value;

        // Mot de passe identique
        if (oldPassword === newPassword) {
            const msg = `Votre nouveau mot de passe est identique à l'ancien.`;
            this.authNoticeService.setNotice(msg, 'danger');
            this.layoutUtilsService.showActionNotification(
                msg,
                MessageType.Update,
                5000,
                true,
                true
            );
            return;
        }

        this.authService.updatePsw(email, oldPassword, newPassword).subscribe({
            next: (res: string) => {
                if (res === "Password updated successfully.") {
                    const msg = `Le mot de passe a été modifié avec succès`;
                    this.authNoticeService.setNotice(msg, 'success');
                    this.layoutUtilsService.showActionNotification(
                        msg,
                        MessageType.Update,
                        5000,
                        true,
                        true
                    );
                    this.loginForm.get('password').setValue(newPassword);
                    this.changePsw$.next(false);
                } else {
                    // cas inattendu
                    const msg = `Réponse inattendue : ${res}`;
                    this.authNoticeService.setNotice(msg, 'warning');
                }
            },
            error: (err) => {
                const errorMsg = `Échec de la mise à jour de votre mot de passe. Veuillez réessayer !`;
                this.authNoticeService.setNotice(errorMsg, 'danger');
                this.layoutUtilsService.showActionNotification(
                    errorMsg,
                    MessageType.Update,
                    5000,
                    true,
                    true
                );
            }
        });
    }

    // Only allow numbers and focus next input
    onInput(event: Event, nextInput: HTMLInputElement | null) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.replace(/[^0-9]/g, ''); // allow only numbers

        if (input.value && nextInput) {
            nextInput.focus();
        }
    }

   // Handle backspace to move focus back
    onKeyDown(event: KeyboardEvent, prevInput: HTMLInputElement | null, currentInput: HTMLInputElement) {
        if (event.key === 'Backspace' && !currentInput.value && prevInput) {
            prevInput.focus();
        }
    }

    // Optional: get full OTP as string
    getFullOtp(): string {
        const otp = this.loginForm.get('otp')?.value;
        return Object.values(otp).join('');
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.authNoticeService.setNotice(null);
        this.unsubscribe.next();
        this.changePsw$.next(false);
        this.unsubscribe.complete();
        this.loading = false;
        this.loginIsLoading$.next(false);
    }
}